import { Color, Fog, Mesh, MeshBasicMaterial, PlaneGeometry, Scene, WebGLRenderer } from "three";
import {
  ASSISTS_ON,
  DT,
  KMH,
  NEUTRAL_INPUT,
  RAD_TO_DEG,
  copyVehicleState,
  createVehicleState,
  driftAngle,
  resetToTrack,
  speedOf,
  step,
  type AssistSettings,
  type CarParams,
  type InputFrame,
  type TrackModel,
  type VehicleState,
} from "@midnight-pass/sim";
import { buildCarModel, type CarModel } from "./carModel";
import { ChaseCamera } from "./chaseCamera";
import { buildCourse } from "./course";
import { KeyboardInput } from "./keyboard";

const SKY = "#070b1a";
const GROUND = "#05070f";
const FOG_NEAR = 60;
const FOG_FAR = 520;
/** Longest frame we'll simulate; anything slower drops time rather than spiralling. */
const MAX_FRAME = 0.1;
const STATS_INTERVAL = 0.1;

/** Everything the UI shows, copied out of the sim on a slow timer. */
export interface GameStats {
  fps: number;
  speedKmh: number;
  gear: number;
  reversing: boolean;
  rpm: number;
  slipFrontDeg: number;
  slipRearDeg: number;
  driftDeg: number;
  input: InputFrame;
  tick: number;
  runSeconds: number;
  distance: number;
  length: number;
  lateral: number;
  crashes: number;
  scrapes: number;
  wallContact: boolean;
  finished: boolean;
  finishSeconds: number;
  crashHold: boolean;
  assists: AssistSettings;
}

export class Game {
  /** Live car parameters. The tuning panel edits this object; the sim reads it every tick. */
  readonly car: CarParams;
  readonly assists: AssistSettings = { ...ASSISTS_ON };
  onStats: ((stats: GameStats) => void) | undefined;
  onToggleOverlay: (() => void) | undefined;
  onToggleTuning: (() => void) | undefined;

  private readonly renderer: WebGLRenderer;
  private readonly scene = new Scene();
  private readonly chase = new ChaseCamera();
  private readonly carModel: CarModel;
  private readonly ground: Mesh;
  private readonly keyboard: KeyboardInput;
  private readonly resizeObserver: ResizeObserver;

  private prev: VehicleState;
  private curr: VehicleState;
  private readonly input: InputFrame = { ...NEUTRAL_INPUT };
  private accumulator = 0;
  private lastTime = 0;
  private raf = 0;
  private fps = 60;
  private statsTimer = 0;
  private wheelSpin = 0;
  private lastCrashes = 0;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly track: TrackModel,
    car: CarParams,
  ) {
    this.car = car;
    this.renderer = new WebGLRenderer({
      canvas,
      antialias: true,
      powerPreference: "high-performance",
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setClearColor(new Color(SKY), 1);

    this.scene.background = new Color(SKY);
    this.scene.fog = new Fog(SKY, FOG_NEAR, FOG_FAR);
    this.scene.add(buildCourse(track));

    this.ground = new Mesh(new PlaneGeometry(8000, 8000), new MeshBasicMaterial({ color: GROUND }));
    this.ground.rotation.x = -Math.PI / 2;
    this.ground.position.y = -0.06;
    this.scene.add(this.ground);

    this.carModel = buildCarModel(car);
    this.scene.add(this.carModel.group);

    this.curr = createVehicleState(track);
    this.prev = { ...this.curr };
    this.lastCrashes = this.curr.crashes;

    this.keyboard = new KeyboardInput(window, (code) => this.handleKey(code));
    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(canvas);
    this.resize();
  }

  start(): void {
    this.lastTime = performance.now();
    this.raf = requestAnimationFrame(this.frame);
  }

  dispose(): void {
    cancelAnimationFrame(this.raf);
    this.keyboard.dispose();
    this.resizeObserver.disconnect();
    this.carModel.dispose();
    this.ground.geometry.dispose();
    this.renderer.dispose();
  }

  /** Back to the centre-line where the car is, facing forward (R). */
  resetToTrack(): void {
    resetToTrack(this.curr, this.track);
    this.afterReset();
  }

  /** Restart the run from the grid (Enter). */
  restart(): void {
    this.curr = createVehicleState(this.track);
    this.afterReset();
  }

  private afterReset(): void {
    copyVehicleState(this.prev, this.curr);
    this.keyboard.centre();
    this.chase.snap();
    this.lastCrashes = this.curr.crashes;
  }

  private handleKey(code: string): void {
    switch (code) {
      case "KeyR":
        this.resetToTrack();
        break;
      case "Enter":
        this.restart();
        break;
      case "Backquote":
        this.onToggleOverlay?.();
        break;
      case "KeyT":
        this.onToggleTuning?.();
        break;
      case "KeyX": {
        // Quick all-assists toggle.
        const on =
          this.assists.counterSteer + this.assists.angleCap + this.assists.throttleSmoothing > 0;
        const value = on ? 0 : 1;
        this.assists.counterSteer = value;
        this.assists.angleCap = value;
        this.assists.throttleSmoothing = value;
        break;
      }
    }
  }

  private resize(): void {
    const w = this.canvas.clientWidth;
    const h = this.canvas.clientHeight;
    this.renderer.setSize(w, h, false);
    this.chase.resize(w, h);
  }

  private frame = (now: number): void => {
    this.raf = requestAnimationFrame(this.frame);
    const dt = Math.min((now - this.lastTime) / 1000, MAX_FRAME);
    this.lastTime = now;
    if (dt > 0) this.fps += (1 / dt - this.fps) * 0.1;

    // Fixed-step simulation; rendering interpolates between the last two states.
    this.accumulator += dt;
    while (this.accumulator >= DT) {
      copyVehicleState(this.prev, this.curr);
      this.keyboard.sample(this.car.steering.speed, this.input);
      step(this.curr, this.input, this.car, this.track, this.assists);
      this.accumulator -= DT;
    }
    this.render(this.accumulator / DT, dt);

    this.statsTimer += dt;
    if (this.statsTimer >= STATS_INTERVAL) {
      this.statsTimer = 0;
      this.onStats?.(this.snapshot());
    }
  };

  private render(alpha: number, dt: number): void {
    // A crash reset teleports the car, so skip interpolating across it and snap the camera.
    if (this.curr.crashes !== this.lastCrashes) {
      this.lastCrashes = this.curr.crashes;
      copyVehicleState(this.prev, this.curr);
      this.chase.snap();
    }
    const p = this.prev;
    const c = this.curr;
    const x = p.x + (c.x - p.x) * alpha;
    const y = p.y + (c.y - p.y) * alpha;
    const z = p.z + (c.z - p.z) * alpha;
    let dyaw = c.yaw - p.yaw;
    dyaw -= Math.PI * 2 * Math.round(dyaw / (Math.PI * 2));
    const yaw = p.yaw + dyaw * alpha;
    const steer = p.steerAngle + (c.steerAngle - p.steerAngle) * alpha;
    const vx = p.vx + (c.vx - p.vx) * alpha;

    const model = this.carModel;
    model.group.position.set(x, y, z);
    model.group.rotation.y = yaw;
    for (const wheel of model.frontWheels) wheel.rotation.y = steer;
    this.wheelSpin += (vx * dt) / this.car.wheelRadius;
    for (const wheel of model.spinners) wheel.rotation.x = this.wheelSpin;

    const slip = driftAngle(c);
    this.chase.update(x, y, z, yaw, slip, speedOf(c), dt);
    this.ground.position.x = x;
    this.ground.position.z = z;
    this.renderer.render(this.scene, this.chase.camera);
  }

  snapshot(): GameStats {
    const s = this.curr;
    return {
      fps: this.fps,
      speedKmh: speedOf(s) * KMH,
      gear: s.gear,
      reversing: s.reversing,
      rpm: s.rpm,
      slipFrontDeg: s.slipFront * RAD_TO_DEG,
      slipRearDeg: s.slipRear * RAD_TO_DEG,
      driftDeg: driftAngle(s) * RAD_TO_DEG,
      input: { ...this.input },
      tick: s.tick,
      runSeconds: s.runTicks * DT,
      distance: s.distance,
      length: this.track.finishDistance,
      lateral: s.lateral,
      crashes: s.crashes,
      scrapes: s.scrapes,
      wallContact: s.wallContact,
      finished: s.finished,
      finishSeconds: s.finishTick * DT,
      crashHold: s.lockTicks > 0,
      assists: { ...this.assists },
    };
  }
}
